// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { MangoQuery, RxDatabase } from 'rxdb'
import { QueryOperator } from '../operators/QueryOperator'
import { MutationOperator } from '../operators/MutationOperator'
import type { QueryOptions } from '../operators/types'
import type { DocumentModel } from '../../models/Document'
import { createId } from '../../index'

export interface DocumentFilters {
  orgId: string
  workspaceId?: string
  projectId?: string
  milestoneId?: string
  taskId?: string
  documentType?: DocumentModel['documentType']
  approved?: boolean
}

export interface CreateDocumentInput {
  title: string
  content: string
  orgId: string
  userId: string
  documentType: DocumentModel['documentType']
  workspaceId?: string
  projectId?: string
  milestoneId?: string
  taskId?: string
  codebaseId?: string
  metadata?: DocumentModel['metadata']
}

export interface UpdateDocumentInput {
  id: string
  updates: Partial<DocumentModel>
}

export class DocumentOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: DocumentFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<DocumentModel> {
    const collection = this.db.collections.documents

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selector: MangoQuery<DocumentModel>['selector'] = {
      orgId: { $eq: filters.orgId },
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId }
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId }
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId }
    }

    if (filters.taskId) {
      selector.taskId = { $eq: filters.taskId }
    }

    if (filters.documentType) {
      selector.documentType = { $eq: filters.documentType }
    }

    if (filters.approved !== undefined) {
      selector.approved = { $eq: filters.approved }
    }

    const queryOptions: QueryOptions<DocumentModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    }

    return new QueryOperator<DocumentModel>(collection, queryOptions)
  }

  async findOne(documentId: string): Promise<DocumentModel | null> {
    const doc = await this.db.collections.documents.findOne(documentId).exec()
    return doc ? (doc.toJSON() as DocumentModel) : null
  }

  async count(filters: DocumentFilters): Promise<number> {
    const collection = this.db.collections.documents
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selector: MangoQuery<DocumentModel>['selector'] = {
      orgId: { $eq: filters.orgId },
    }

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId }
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId }
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId }
    }

    if (filters.taskId) {
      selector.taskId = { $eq: filters.taskId }
    }

    if (filters.documentType) {
      selector.documentType = { $eq: filters.documentType }
    }

    if (filters.approved !== undefined) {
      selector.approved = { $eq: filters.approved }
    }

    return await collection.count({ selector }).exec()
  }

  createMutation(): MutationOperator<DocumentModel, CreateDocumentInput> {
    return new MutationOperator<DocumentModel, CreateDocumentInput>(this.db.collections.documents, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString()
        const document: DocumentModel = {
          id: createId('documents'),
          orgId: input.orgId,
          workspaceId: input.workspaceId ?? '',
          projectId: input.projectId ?? '',
          milestoneId: input.milestoneId ?? '',
          taskId: input.taskId ?? '',
          codebaseId: input.codebaseId ?? '',
          userId: input.userId,
          title: input.title,
          content: input.content,
          documentType: input.documentType,
          documentVersion: 1,
          approved: false,
          createdAt: now,
          updatedAt: now,
          ...(input.metadata && { metadata: input.metadata }),
        }

        await collection.insert(document)
        return document
      },
    })
  }

  updateMutation(): MutationOperator<DocumentModel, UpdateDocumentInput> {
    return new MutationOperator<DocumentModel, UpdateDocumentInput>(this.db.collections.documents, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec()
        if (!doc) throw new Error(`Document ${id} not found`)

        await doc.update({
          $set: { ...updates, updatedAt: new Date().toISOString() },
        })

        return doc.toJSON() as DocumentModel
      },
    })
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.documents, {
      mutationFn: async (collection, documentId) => {
        const doc = await collection.findOne(documentId).exec()
        if (!doc) throw new Error(`Document ${documentId} not found`)

        await doc.remove()
      },
    })
  }

  approveMutation(): MutationOperator<DocumentModel, { id: string; userId: string }> {
    return new MutationOperator<DocumentModel, { id: string; userId: string }>(
      this.db.collections.documents,
      {
        mutationFn: async (collection, { id, userId }) => {
          const doc = await collection.findOne(id).exec()
          if (!doc) throw new Error(`Document ${id} not found`)

          const now = new Date().toISOString()
          await doc.update({
            $set: {
              approved: true,
              approvedAt: now,
              approvedBy: userId,
              updatedAt: now,
            },
          })

          return doc.toJSON() as DocumentModel
        },
      }
    )
  }
}
